import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { LocateFixed, Loader2, Search, SlidersHorizontal, ArrowRight, Camera } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { toast } from '@/components/ui/sonner';
import { SEO } from '@/components/SEO';
import { Reveal } from '@/components/Reveal';
import { TextReveal } from '@/components/TextReveal';
import { SkeletonList } from '@/components/SkeletonList';
import { PhotographerCard } from '@/components/marketplace/PhotographerCard';
import { DirectoryFilters, DISTANCE_MAX, PRICE_MAX } from '@/components/marketplace/DirectoryFilters';
import { getMarketplaceStyles, searchPhotographers } from '@/lib/api';
import { PHOTOGRAPHERS_PAGE } from '@/constants/testIds';

const DEFAULT_FILTERS = { styles: [], price: [0, PRICE_MAX], distance: DISTANCE_MAX, verifiedOnly: false, availableOn: '', sort: 'distance' };
const fieldClass = 'bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] h-11 focus-visible:ring-[hsl(var(--lxra-gold))]';

const countActive = (f) =>
  (f.styles.length ? 1 : 0) + (f.price[0] > 0 || f.price[1] < PRICE_MAX ? 1 : 0) + (f.distance < DISTANCE_MAX ? 1 : 0) + (f.verifiedOnly ? 1 : 0) + (f.availableOn ? 1 : 0);

export default function Photographers() {
  const [styles, setStyles] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [locationText, setLocationText] = useState('');
  const [origin, setOrigin] = useState(null); // {lat, lng, label} | {query}
  const [locating, setLocating] = useState(false);
  const [state, setState] = useState({ status: 'loading', results: [], total: 0, originLabel: null });
  const requestId = useRef(0);

  useEffect(() => { getMarketplaceStyles().then(setStyles).catch(() => setStyles([])); }, []);

  const run = useCallback(async () => {
    const id = ++requestId.current;
    setState((s) => ({ ...s, status: 'loading' }));
    const params = { sort: filters.sort, verified_only: filters.verifiedOnly || undefined };
    if (filters.styles.length) params.style = filters.styles;
    if (filters.price[0] > 0) params.min_price = filters.price[0] * 100;
    if (filters.price[1] < PRICE_MAX) params.max_price = filters.price[1] * 100;
    if (filters.availableOn) params.available_on = filters.availableOn;
    if (origin?.lat != null) { params.lat = origin.lat; params.lng = origin.lng; }
    else if (origin?.query) params.location = origin.query;
    if (origin && filters.distance < DISTANCE_MAX) params.radius_miles = filters.distance;
    try {
      const data = await searchPhotographers(params);
      if (id !== requestId.current) return;
      setState({ status: 'ready', results: data.results, total: data.total, originLabel: data.origin?.label || origin?.label || null });
    } catch {
      if (id !== requestId.current) return;
      setState((s) => ({ ...s, status: 'error' }));
    }
  }, [filters, origin]);

  useEffect(() => { run(); }, [run]);

  const submitLocation = (e) => {
    e?.preventDefault?.();
    const q = locationText.trim();
    setOrigin(q ? { query: q, label: q } : null);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error('Your browser does not support location.'); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setLocating(false); setLocationText('My location'); setOrigin({ lat: pos.coords.latitude, lng: pos.coords.longitude, label: 'your location' }); },
      () => { setLocating(false); toast.error('We could not read your location. Type a city or ZIP instead.'); },
      { timeout: 8000 },
    );
  };

  const update = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const clear = () => setFilters((f) => ({ ...DEFAULT_FILTERS, sort: f.sort }));
  const active = useMemo(() => countActive(filters), [filters]);

  return (
    <div className="pt-16 bg-[#0A0A0A]" data-testid={PHOTOGRAPHERS_PAGE.root}>
      <SEO title="Find a Photographer" description="Browse the LXRA network of verified DMV photographers by location, style and starting rate." />

      {/* Header */}
      <section data-testid={PHOTOGRAPHERS_PAGE.hero} className="relative lxra-grain border-b border-[hsl(var(--lxra-border))]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 pt-14 pb-10 sm:pt-16">
          <Reveal>
            <p className="text-xs tracking-[0.22em] uppercase text-[hsl(var(--lxra-gold))]">LXRA Marketplace</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-[0.06em] leading-[0.95] text-[hsl(var(--lxra-fg))] mt-3">
              <TextReveal text="FIND YOUR PHOTOGRAPHER" />
            </h1>
            <div className="mt-4 h-[2px] w-16 bg-[hsl(var(--lxra-gold))]" />
            <p className="mt-5 max-w-xl text-sm sm:text-base text-[hsl(var(--lxra-muted))] leading-relaxed">
              Curated DMV talent — editorial style, verified standards, and transparent starting rates.
            </p>
          </Reveal>

          <Reveal delay={0.08}>
            <form onSubmit={submitLocation} className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto_auto_auto] items-center">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--lxra-muted))]" />
                <Input
                  value={locationText}
                  onChange={(e) => setLocationText(e.target.value)}
                  placeholder="City, neighborhood, or ZIP"
                  aria-label="Location"
                  data-testid={PHOTOGRAPHERS_PAGE.locationInput}
                  className={`${fieldClass} pl-9`}
                />
              </div>
              <Button type="submit" className="h-11 px-5 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.1em] font-semibold" data-testid="photographers-filter-location-submit">
                Search
              </Button>
              <Button type="button" variant="outline" onClick={useMyLocation} disabled={locating} data-testid={PHOTOGRAPHERS_PAGE.useMyLocation} className="h-11 px-4 rounded-lg border-[hsl(var(--lxra-border))] bg-transparent text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] text-xs uppercase tracking-[0.1em]">
                {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <LocateFixed className="h-4 w-4 mr-2" />}
                {locating ? '' : 'Use my location'}
              </Button>
              <Select value={filters.sort} onValueChange={(v) => update({ sort: v })}>
                <SelectTrigger data-testid={PHOTOGRAPHERS_PAGE.sortSelect} className={`${fieldClass} w-full sm:w-44`} aria-label="Sort">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]">
                  <SelectItem value="distance">{origin ? 'Distance' : 'Recommended'}</SelectItem>
                  <SelectItem value="price">Price: low to high</SelectItem>
                  <SelectItem value="name">Name</SelectItem>
                </SelectContent>
              </Select>
            </form>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-10 lg:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8 lg:gap-12">
          {/* Filter rail (desktop) */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-6">
              <DirectoryFilters styles={styles} filters={filters} onChange={update} hasOrigin={Boolean(origin)} onClear={clear} activeCount={active} />
            </div>
          </aside>

          <div>
            <div className="flex items-center justify-between gap-4 mb-6">
              <p data-testid={PHOTOGRAPHERS_PAGE.resultsCount} className="text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))]">
                {state.status === 'loading' ? 'Searching…' : `${state.total} photographer${state.total === 1 ? '' : 's'}`}
                {state.originLabel && state.status !== 'loading' && (
                  <span data-testid={PHOTOGRAPHERS_PAGE.originLabel}> · near {state.originLabel}</span>
                )}
              </p>
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" data-testid={PHOTOGRAPHERS_PAGE.filtersOpen} className="lg:hidden h-10 rounded-lg border-[hsl(var(--lxra-border))] bg-transparent text-[hsl(var(--lxra-fg))] text-xs uppercase tracking-[0.1em]">
                    <SlidersHorizontal className="h-4 w-4 mr-2" /> Filters{active ? ` (${active})` : ''}
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="bg-[#0F0F10] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] overflow-y-auto">
                  <SheetHeader><SheetTitle className="sr-only">Filters</SheetTitle></SheetHeader>
                  <div className="mt-4">
                    <DirectoryFilters styles={styles} filters={filters} onChange={update} hasOrigin={Boolean(origin)} onClear={clear} activeCount={active} />
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            {state.status === 'loading' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6">
                <SkeletonList count={6} className="h-[26rem] rounded-2xl bg-[#141416]" />
              </div>
            )}

            {state.status === 'error' && (
              <div data-testid={PHOTOGRAPHERS_PAGE.resultsError} className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-10 text-center">
                <h2 className="font-display text-2xl tracking-[0.06em] text-[hsl(var(--lxra-fg))]">WE COULDN’T LOAD PHOTOGRAPHERS</h2>
                <p className="mt-2 text-sm text-[hsl(var(--lxra-muted))]">Please check your connection and try again.</p>
                <Button onClick={run} data-testid={PHOTOGRAPHERS_PAGE.resultsRetry} className="mt-6 h-10 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.1em] font-semibold">Retry</Button>
              </div>
            )}

            {state.status === 'ready' && state.results.length === 0 && (
              <div data-testid={PHOTOGRAPHERS_PAGE.resultsEmpty} className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-10 text-center">
                <Camera className="h-8 w-8 text-[hsl(var(--lxra-muted))] mx-auto" />
                <h2 className="font-display text-2xl tracking-[0.06em] text-[hsl(var(--lxra-fg))] mt-4">NO MATCHES IN THIS RADIUS</h2>
                <p className="mt-2 text-sm text-[hsl(var(--lxra-muted))]">Try expanding distance or removing a style filter.</p>
                <Button variant="outline" onClick={clear} className="mt-6 h-10 rounded-lg border-[hsl(var(--lxra-border))] bg-transparent text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] text-xs uppercase tracking-[0.1em]">Clear filters</Button>
              </div>
            )}

            {state.status === 'ready' && state.results.length > 0 && (
              <div data-testid={PHOTOGRAPHERS_PAGE.resultsGrid} className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6">
                {state.results.map((s, i) => <PhotographerCard key={s.id} studio={s} index={i} />)}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Apply to join */}
      <section data-testid={PHOTOGRAPHERS_PAGE.applySection} className="border-t border-[hsl(var(--lxra-gold))]/30 bg-[#0F0F10]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8">
          <Reveal>
            <p className="text-xs tracking-[0.2em] uppercase text-[hsl(var(--lxra-gold))]">Photographer?</p>
            <h2 className="font-display text-3xl sm:text-4xl mt-2 tracking-[0.06em] text-[hsl(var(--lxra-fg))]">JOIN THE LXRA NETWORK.</h2>
            <p className="mt-3 max-w-lg text-sm text-[hsl(var(--lxra-muted))] leading-relaxed">
              Invite-only or apply for review. Approved studios get a public profile, their own dashboard, and booking requests routed directly to them.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/signup/photographer?apply=1"
              data-testid={PHOTOGRAPHERS_PAGE.applySectionButton}
              className="inline-flex items-center h-12 px-7 rounded-xl bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] transition-colors text-xs uppercase tracking-[0.1em] font-semibold whitespace-nowrap"
            >
              Apply to join <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
